import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { fetchTickets, updateTicketStatus } from '../../services/supportService';
import TicketDetailModal from './TicketDetailModal';

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  margin-bottom: var(--space-lg);
  padding-bottom: var(--space-md);
  border-bottom: 1px solid var(--color-border);
`;

const Title = styled.h1`
  font-size: var(--font-size-3xl);
  font-family: var(--font-serif);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
  margin: 0;
`;

const Subtitle = styled.p`
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  margin: 0;
`;

const SummaryRow = styled.div`
  display: flex;
  gap: var(--space-sm);
  align-items: center;
  flex-wrap: wrap;
`;

const SummaryBadge = styled.span`
  background: var(--color-surface);
  color: var(--color-accent);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  padding: var(--space-xs) var(--space-sm);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
`;

const ControlsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
  align-items: center;
  justify-content: space-between;
`;

const FiltersGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
  align-items: center;
`;

const SearchInput = styled.input`
  height: 46px;
  padding: var(--space-xs) var(--space-sm);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  min-width: 250px;
  background: var(--color-surface);
  color: var(--color-text);

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 4px rgba(0, 24, 206, 0.08);
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;

  &::after {
    content: '';
    position: absolute;
    top: calc(50% - 1px);
    right: var(--space-sm);
    width: 8px;
    height: 8px;
    border-right: 2px solid var(--color-text-secondary);
    border-bottom: 2px solid var(--color-text-secondary);
    transform: translateY(-50%) rotate(45deg);
    pointer-events: none;
    transition: border-color var(--transition-speed) ease;
  }

  &:focus-within::after {
    border-color: var(--color-accent);
  }
`;

const StatusSelect = styled.select`
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  height: 46px;
  padding: var(--space-xs) var(--space-xl) var(--space-xs) var(--space-sm);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  background: white;
  color: var(--color-text);
  cursor: pointer;
  transition: border-color var(--transition-speed) ease, box-shadow var(--transition-speed) ease;

  &:hover {
    border-color: var(--color-accent);
  }

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 4px rgba(0, 24, 206, 0.08);
  }
`;

const RefreshButton = styled.button`
  border: 1px solid var(--color-border);
  background: white;
  color: var(--color-text-secondary);
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  
  &:hover {
    border-color: var(--color-accent);
    color: var(--color-primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TableWrapper = styled.div`
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  overflow: hidden;
  overflow-x: auto;
`;

const TicketsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeadCell = styled.th`
  text-align: left;
  padding: var(--space-sm) var(--space-md);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  background: var(--color-primary-lighter);
  border-bottom: 1px solid var(--color-border);
  white-space: nowrap;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background: rgba(247, 247, 243, 0.72);
  }

  &:hover {
    background: rgba(130, 172, 216, 0.08);
  }
`;

const TableCell = styled.td`
  padding: var(--space-md);
  font-size: var(--font-size-md);
  color: var(--color-text);
  border-bottom: 1px solid var(--color-border-light);
  vertical-align: top;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.02em;
  text-transform: capitalize;
  
  background: ${props => {
    switch(props.status) {
      case 'open': return 'rgba(130, 172, 216, 0.16)';
      case 'pending': return 'rgba(180, 83, 9, 0.12)';
      case 'closed': return 'rgba(93, 64, 34, 0.08)';
      default: return 'rgba(93, 64, 34, 0.08)';
    }
  }};
  
  color: ${props => {
    switch(props.status) {
      case 'open': return 'var(--color-accent)';
      case 'pending': return 'var(--color-warning-dark)';
      case 'closed': return 'var(--color-text-muted)';
      default: return 'var(--color-text-muted)';
    }
  }};
`;

const EmptyState = styled.div`
  padding: var(--space-xl);
  text-align: center;
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
`;

const ErrorBox = styled.div`
  padding: var(--space-lg);
  border: 1px solid var(--color-error);
  border-radius: var(--border-radius-md);
  background: rgba(180, 35, 24, 0.08);
  color: var(--color-error);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-md);
`;

const SubjectLink = styled.button`
  font-weight: var(--font-weight-medium);
  background: none;
  border: none;
  padding: 0;
  color: var(--color-primary);
  text-align: left;
  cursor: pointer;
  font-size: var(--font-size-md);
  
  &:hover {
    color: var(--color-accent);
  }
  
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 500px;
  display: inline-block;
`;

const MetaText = styled.div`
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const SupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal state
  const [selectedTicket, setSelectedTicket] = useState(null);

  const loadTickets = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTickets();
      setTickets(data);
    } catch (err) {
      setError('Failed to load tickets. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const handleUpdateStatus = async (ticketId, newStatus) => {
    try {
      await updateTicketStatus(ticketId, newStatus);
      // Optimistically update local state or reload
      setTickets(prev => prev.map(t => 
        t.id === ticketId ? { ...t, status: newStatus } : t
      ));
    } catch (err) {
      console.error(err);
      alert('Failed to update status');
    }
  };

  const filteredTickets = useMemo(() => {
    const normalizedTerm = searchTerm.trim().toLowerCase();

    return tickets.filter((ticket) => {
      // Status Filter
      if (statusFilter !== 'all' && ticket.status !== statusFilter) {
        return false;
      }

      // Search Filter
      if (!normalizedTerm) return true;

      return (
        ticket.subject.toLowerCase().includes(normalizedTerm) ||
        ticket.email.toLowerCase().includes(normalizedTerm) ||
        ticket.userId.toLowerCase().includes(normalizedTerm) ||
        ticket.message.toLowerCase().includes(normalizedTerm)
      );
    });
  }, [tickets, searchTerm, statusFilter]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <PageContainer>
      <PageHeader>
        <Title>Support Tickets</Title>
        <Subtitle>View and manage customer support requests.</Subtitle>
        <SummaryRow>
          <SummaryBadge>{tickets.length} total</SummaryBadge>
          {(searchTerm.trim() || statusFilter !== 'all') && (
            <SummaryBadge>{filteredTickets.length} showing</SummaryBadge>
          )}
          <RefreshButton onClick={loadTickets} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </RefreshButton>
        </SummaryRow>
        <ControlsRow>
          <FiltersGroup>
            <SearchInput
              type="search"
              placeholder="Search subject, email, ID..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              aria-label="Search tickets"
            />
            <SelectWrapper>
              <StatusSelect
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                aria-label="Filter tickets by status"
              >
                <option value="all">All Statuses</option>
                <option value="open">Open</option>
                <option value="pending">Pending</option>
                <option value="closed">Closed</option>
              </StatusSelect>
            </SelectWrapper>
          </FiltersGroup>
        </ControlsRow>
      </PageHeader>

      {error ? (
        <ErrorBox role="alert">
          <span>{error}</span>
          <RefreshButton onClick={loadTickets}>Try again</RefreshButton>
        </ErrorBox>
      ) : (
        <TableWrapper aria-busy={loading}>
          {loading ? (
            <EmptyState>Loading tickets...</EmptyState>
          ) : tickets.length === 0 ? (
            <EmptyState>No tickets found.</EmptyState>
          ) : filteredTickets.length === 0 ? (
            <EmptyState>No tickets match your search or filter.</EmptyState>
          ) : (
            <TicketsTable>
              <thead>
                <tr>
                  <TableHeadCell scope="col">Subject</TableHeadCell>
                  <TableHeadCell scope="col">User</TableHeadCell>
                  <TableHeadCell scope="col">Date</TableHeadCell>
                  <TableHeadCell scope="col">Status</TableHeadCell>
                </tr>
              </thead>
              <tbody>
                {filteredTickets.map((ticket) => (
                  <TableRow key={ticket.id}>
                    <TableCell>
                      <SubjectLink onClick={() => setSelectedTicket(ticket)}>
                        {ticket.subject}
                      </SubjectLink>
                    </TableCell>
                    <TableCell>
                      <div>{ticket.email}</div>
                      <MetaText title={ticket.userId}>ID: {ticket.userId.substring(0, 8)}...</MetaText>
                    </TableCell>
                    <TableCell>
                      {formatDate(ticket.createdAt)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={ticket.status}>
                        {ticket.status}
                      </StatusBadge>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </TicketsTable>
          )}
        </TableWrapper>
      )}

      {selectedTicket && (
        <TicketDetailModal 
          ticket={selectedTicket} 
          onClose={() => setSelectedTicket(null)}
          onUpdateStatus={handleUpdateStatus}
        />
      )}
    </PageContainer>
  );
};

export default SupportTickets;
